import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-block;
      vertical-align: middle;
    }
    :host([fluid]) { display: block; width: 100%; }

    /* ── Wrapper ────────────────────────────────────────────────────────────── */
    .uwc-input {
      --_bg:        var(--uwc-input-bg,                ${t.surface});
      --_border:    var(--uwc-input-border,            1px solid ${t.borderControl});
      --_radius:    var(--uwc-input-radius,            ${t.radiusMd});
      --_color:     var(--uwc-input-color,             ${t.text});
      --_ph-color:  var(--uwc-input-placeholder-color, ${t.textMuted});
      --_font-size: var(--uwc-input-font-size,         ${t.fontSizeMd});
      --_px:        var(--uwc-input-padding-x,         ${t.space3});
      --_py:        var(--uwc-input-padding-y,         0);
      --_icon-gap:  ${t.space2};

      display: flex;
      align-items: center;
      width: 100%;
      min-height:    ${t.controlHeightMd};
      background:    var(--_bg);
      border:        var(--_border);
      border-radius: var(--_radius);
      overflow: hidden;
      transition: border-color ${t.durationBase}, box-shadow ${t.durationBase};
    }

    .uwc-input:focus-within {
      border-color: var(--uwc-color-primary, ${t.primary});
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* ── Variants ───────────────────────────────────────────────────────────── */
    .uwc-input--filled {
      --_bg: var(--uwc-input-filled-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    .uwc-input--filled:focus-within {
      --_bg: var(--uwc-input-bg, ${t.surface});
      border-color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Invalid ────────────────────────────────────────────────────────────── */
    .uwc-input--invalid {
      border-color: var(--uwc-color-danger, ${t.danger}) !important;
    }
    .uwc-input--invalid:focus-within {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 20%, transparent);
    }

    /* ── Sizes ──────────────────────────────────────────────────────────────── */
    .uwc-input--small { --_font-size: ${t.fontSizeXs}; --_px: ${t.space2};  --_py: 0.3125rem; min-height: ${t.controlHeightSm}; }
    .uwc-input--large { --_font-size: ${t.fontSizeLg}; --_px: ${t.space4};    --_py: 0.6875rem; min-height: ${t.controlHeightLg}; }

    /* ── Disabled ───────────────────────────────────────────────────────────── */
    .uwc-input--disabled { pointer-events: none; opacity: 0.6; }

    /* ── Native input ───────────────────────────────────────────────────────── */
    input {
      flex: 1;
      min-width: 0;
      padding-inline: var(--_px);
      padding-block:  var(--_py);
      font-family: var(--uwc-font-family, inherit);
      font-size:   var(--_font-size);
      line-height: 1;
      color:       var(--_color);
      background:  transparent;
      border:      none;
      outline:     none;
    }
    input::placeholder { color: var(--_ph-color); }
    input:disabled      { cursor: not-allowed; }

    /* ── Icon slots ─────────────────────────────────────────────────────────── */
    .uwc-input__prefix,
    .uwc-input__suffix {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      color: var(--_ph-color);
      /* Drive font-size so a slotted icon (sized in em) tracks the input's
         font — and therefore the size attr and the global size/font presets —
         instead of the inherited page default. */
      font-size: var(--_font-size);
    }
    .uwc-input__prefix { padding-inline-start: var(--_px); }
    .uwc-input__suffix { padding-inline-end:   var(--_px); }

    /* Shrink native input padding when slot is present */
    :host([has-prefix]) input { padding-inline-start: var(--_icon-gap); }
    :host([has-suffix]) input { padding-inline-end:   var(--_icon-gap); }

    /* ── Float label ─────────────────────────────────────────────────────────── */

    /* Shared wrapper positioning context */
    .uwc-input--float { position: relative; }

    /* Shared label base — each variant overrides top / transform / background */
    .uwc-input__label {
      position:       absolute;
      left:           calc(var(--_px) + 0.125rem);
      line-height:    1;
      white-space:    nowrap;
      pointer-events: none;
      padding-inline: 0.25rem;
      transition:
        top       ${t.durationBase} ${t.easingStandard},
        font-size ${t.durationBase} ${t.easingStandard},
        color     ${t.durationBase} ${t.easingStandard},
        transform ${t.durationBase} ${t.easingStandard};
    }

    /* ── "over" (default): label floats fully ABOVE the input with gap ─── */
    /* translateY(calc(-100% - 0.375rem)) lifts the label so its bottom edge
       sits 0.375 rem above the top border, giving clear breathing room.      */
    .uwc-input--float-over {
      overflow:   visible;
      margin-top: 1.5rem;   /* layout space for the label above the border */
    }
    .uwc-input--float-over .uwc-input__label {
      top:        50%;
      transform:  translateY(-50%);
      font-size:  var(--_font-size);
      color:      var(--_ph-color);
      background: transparent;
    }
    .uwc-input--float-over:focus-within .uwc-input__label,
    .uwc-input--float-over input:not(:placeholder-shown) ~ .uwc-input__label {
      top:       0;
      transform: translateY(calc(-100% - 0.375rem));   /* gap above the border */
      font-size: calc(var(--_font-size) * 0.85);
      color:     var(--uwc-input-label-active-color, var(--uwc-color-primary, ${t.primary}));
    }

    /* ── "on": label sits ON (straddles) the upper border ───────────── */
    /* top:0 + translateY(-50%) centres the label on the top border line.
       overflow:visible + margin-top expose the half that protrudes above. */
    .uwc-input--float-on {
      overflow:   visible;
      margin-top: 0.75rem;
    }
    .uwc-input--float-on .uwc-input__label {
      top:        50%;
      transform:  translateY(-50%);
      font-size:  var(--_font-size);
      color:      var(--_ph-color);
      background: var(--_bg);   /* masks the border line beneath the label */
    }
    .uwc-input--float-on:focus-within .uwc-input__label,
    .uwc-input--float-on input:not(:placeholder-shown) ~ .uwc-input__label {
      top:       0;
      transform: translateY(-50%);   /* stays centred on top border */
      font-size: calc(var(--_font-size) * 0.8);
      color:     var(--uwc-input-label-active-color, var(--uwc-color-primary, ${t.primary}));
    }
    .uwc-input--float-on.uwc-input--filled .uwc-input__label {
      background: var(--uwc-input-filled-bg, ${t.surfaceRaised});
    }

    /* ── "in": label floats to the interior top, never leaves the border ─ */
    /* Extra input padding creates room so the small label and input text
       never overlap. The label at rest centres in the lower text area.      */
    .uwc-input--float-in input {
      padding-top:    1.375rem;
      padding-bottom: 0.3125rem;
    }
    .uwc-input--float-in .uwc-input__label {
      top:            50%;
      transform:      translateY(-50%);
      font-size:      var(--_font-size);
      color:          var(--_ph-color);
      background:     transparent;
      left:           var(--_px);   /* align with input text start */
      padding-inline: 0;            /* no border-masking needed inside */
    }
    .uwc-input--float-in:focus-within .uwc-input__label,
    .uwc-input--float-in input:not(:placeholder-shown) ~ .uwc-input__label {
      top:            0.3125rem;   /* clearly inside, below the border */
      transform:      none;
      font-size:      calc(var(--_font-size) * 0.75);
      color:          var(--uwc-input-label-active-color, var(--uwc-color-primary, ${t.primary}));
      left:           var(--_px);
      padding-inline: 0;
    }

    /* ── Invalid label color (all variants) ─────────────────────────────── */
    .uwc-input--invalid .uwc-input__label,
    .uwc-input--invalid:focus-within .uwc-input__label,
    .uwc-input--invalid input:not(:placeholder-shown) ~ .uwc-input__label {
      color: var(--uwc-color-danger, ${t.danger});
    }

    /* ── Prefix offset ───────────────────────────────────────────────────── */
    :host([has-prefix]) .uwc-input__label {
      left: calc(var(--_px) + var(--_icon-gap) + 1.25rem);
    }
    /* "in" variant: label aligns with input text, adjust for prefix */
    :host([has-prefix]) .uwc-input--float-in .uwc-input__label,
    :host([has-prefix]) .uwc-input--float-in:focus-within .uwc-input__label,
    :host([has-prefix]) .uwc-input--float-in input:not(:placeholder-shown) ~ .uwc-input__label {
      left: calc(var(--_icon-gap) + 1.25rem);
    }
  `,
];
